import {
  Check,
  X,
  AlertTriangle,
  CircleSlash,
  Lightbulb,
  ShieldCheck,
  Loader2,
  ArrowRight,
} from "lucide-react";

const STATUS_META = {
  verified: {
    label: "Verificado",
    icon: Check,
    ring: "stroke-forest",
    bg: "bg-forest text-paper",
    pill: "bg-forest/10 text-forest",
    percent: 100,
  },
  weak: {
    label: "Débil",
    icon: AlertTriangle,
    ring: "stroke-copper",
    bg: "bg-copper text-paper",
    pill: "bg-[#FBF6EC] text-copper",
    percent: 50,
  },
  missing: {
    label: "Faltante",
    icon: CircleSlash,
    ring: "stroke-[#B6533F]",
    bg: "bg-[#B6533F] text-paper",
    pill: "bg-[#FBE9E5] text-[#B6533F]",
    percent: 0,
  },
  misaligned: {
    label: "Desalineado",
    icon: X,
    ring: "stroke-[#B6533F]",
    bg: "bg-[#B6533F] text-paper",
    pill: "bg-[#FBE9E5] text-[#B6533F]",
    percent: 20,
  },
};

const PRIORITY_META = {
  high: { label: "Alta", color: "text-[#B6533F]", dot: "bg-[#B6533F]" },
  medium: { label: "Media", color: "text-copper", dot: "bg-copper" },
  low: { label: "Baja", color: "text-forest", dot: "bg-forest" },
};

const FIX_AREA_LABEL = {
  research_question: "Pregunta de investigación",
  objectives: "Objetivos",
  methodology: "Metodología",
  general: "General",
};

function MiniRing({ percent, status }) {
  const meta = STATUS_META[status] || STATUS_META.weak;
  return (
    <div className="relative h-9 w-9 shrink-0">
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8E6E1" strokeWidth="3.5" />
        <circle
          cx="18"
          cy="18"
          r="15.9"
          fill="none"
          strokeWidth="3.5"
          strokeDasharray={`${percent}, 100`}
          strokeLinecap="round"
          className={meta.ring}
        />
      </svg>
      <span
        className={`absolute inset-0 grid place-items-center rounded-full ${meta.bg.replace(
          "text-paper",
          ""
        )} text-paper`}
        style={{ width: 18, height: 18, top: 9, left: 9 }}
      >
        <meta.icon size={10} strokeWidth={3} />
      </span>
    </div>
  );
}

function BigRing({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const tone = s >= 75 ? "text-forest" : s >= 50 ? "text-copper" : "text-[#B6533F]";
  return (
    <div className="relative h-28 w-28 shrink-0">
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E8E6E1" strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r="15.9"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={`${s}, 100`}
          strokeLinecap="round"
          className={tone}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center leading-none">
          <p className="font-serif text-3xl text-ink">{s}</p>
          <p className="text-[9px] tracking-[0.22em] uppercase text-ink-soft/80 mt-0.5">/ 100</p>
        </div>
      </div>
    </div>
  );
}

export default function CoherencePanel({ report, loading, onRevalidate, onGoToContext, error }) {
  if (loading) {
    return (
      <div className="h-full grid place-items-center p-6" data-testid="coherence-panel-loading">
        <div className="text-center">
          <Loader2 size={26} className="mx-auto animate-spin text-forest" />
          <p className="mt-3 font-serif text-lg text-ink">Analizando coherencia…</p>
          <p className="mt-1 text-[12px] text-ink-soft">
            El metodólogo IA está revisando tu pregunta, objetivos y método.
          </p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full grid place-items-center p-6" data-testid="coherence-panel-error">
        <div className="text-center max-w-xs">
          <span className="grid place-items-center h-10 w-10 rounded-full bg-[#FBE9E5] text-[#B6533F] mx-auto">
            <AlertTriangle size={16} />
          </span>
          <p className="mt-3 font-serif text-lg text-ink">{error}</p>
          {onGoToContext && (
            <button
              onClick={onGoToContext}
              data-testid="coherence-fix-context"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-medium text-paper bg-forest hover:bg-forest-dark"
            >
              Ir a Contexto <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="h-full grid place-items-center p-6 bg-paper-2/30" data-testid="coherence-panel-empty">
        <div className="text-center max-w-xs">
          <span className="grid place-items-center h-12 w-12 rounded-full bg-forest text-paper mx-auto">
            <ShieldCheck size={18} />
          </span>
          <p className="mt-4 font-serif text-lg text-ink">Aún no has validado coherencia</p>
          <p className="mt-1.5 text-[12.5px] text-ink-soft">
            Define pregunta, objetivos y metodología, escribe al menos 80 caracteres en el editor y presiona <span className="font-medium text-forest">Validar coherencia</span> arriba.
          </p>
        </div>
      </div>
    );
  }

  const score = report.metodologia_score ?? report.alignment_score ?? 0;
  return (
    <div className="h-full overflow-y-auto bg-paper-2/20" data-testid="coherence-panel">
      {/* Header with big ring */}
      <div className="px-5 pt-5 pb-4 border-b border-rule bg-white">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
          Análisis del metodólogo IA
        </p>
        <div className="mt-3 flex items-start gap-4">
          <BigRing score={score} />
          <div className="min-w-0 flex-1">
            <p className="font-serif text-base text-ink leading-snug" data-testid="coherence-summary">
              {report.summary || "Análisis disponible."}
            </p>
            <p className="mt-2 text-[10.5px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
              Metodología score
            </p>
            <p className="text-[11px] text-ink-soft">
              {score >= 75
                ? "Coherencia sólida."
                : score >= 50
                ? "Coherencia parcial, requiere ajustes."
                : "Coherencia insuficiente. Revisa estructura."}
            </p>
          </div>
        </div>
        {onRevalidate && (
          <button
            data-testid="coherence-revalidate"
            onClick={onRevalidate}
            className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-forest hover:text-forest-dark"
          >
            <ShieldCheck size={11} /> Re-evaluar
          </button>
        )}
      </div>

      {/* Checklist */}
      <div className="px-5 py-4">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
          Checklist de secciones verificadas
        </p>
        <ul className="flex flex-col gap-2.5" data-testid="coherence-checklist">
          {(report.checklist || []).map((c, i) => {
            const meta = STATUS_META[c.status] || STATUS_META.weak;
            return (
              <li
                key={i}
                data-testid={`coherence-check-${i}`}
                className="flex items-start gap-3 rounded-xl border border-rule bg-white px-3 py-2.5"
              >
                <MiniRing percent={meta.percent} status={c.status} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-[14px] text-ink leading-tight">{c.label}</p>
                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase font-medium ${meta.pill}`}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] text-ink-soft leading-snug">{c.note}</p>
                </div>
              </li>
            );
          })}
          {(report.checklist || []).length === 0 && (
            <li className="text-[12px] text-ink-soft italic">Sin checklist devuelto.</li>
          )}
        </ul>
      </div>

      {/* Guidance */}
      <div className="px-5 pb-6">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
          Cómo arreglar las inconsistencias
        </p>
        <ul className="flex flex-col gap-2.5" data-testid="coherence-guidance">
          {(report.guidance || []).map((g, i) => {
            const prio = PRIORITY_META[g.priority] || PRIORITY_META.medium;
            return (
              <li
                key={i}
                data-testid={`coherence-guidance-${i}`}
                className="rounded-xl border border-copper/30 bg-[#FBF6EC] px-3 py-2.5 flex gap-3"
              >
                <span className="shrink-0 grid place-items-center h-7 w-7 rounded-full bg-copper text-paper mt-0.5">
                  <Lightbulb size={12} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-[14px] text-ink leading-tight">{g.title}</p>
                    <span className={`inline-flex items-center gap-1 text-[9.5px] tracking-[0.18em] uppercase font-medium ${prio.color}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${prio.dot}`} /> {prio.label}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10.5px] text-ink-soft/80">
                    Área: <span className="text-ink">{FIX_AREA_LABEL[g.fix_area] || g.fix_area}</span>
                  </p>
                  <p className="mt-1 text-[12px] text-ink-soft leading-snug">{g.detail}</p>
                </div>
              </li>
            );
          })}
          {(report.guidance || []).length === 0 && (
            <li className="text-[12px] text-ink-soft italic">Sin sugerencias devueltas.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
